import { useEffect, useRef, useState } from 'react';
import { validateSubdomainFormat } from '@shared/utils/subdomain';
import { checkSubdomain } from '@shared/services/subdomainService';

/**
 * Debounced live subdomain validation, shared by the signup form and the profile subdomain editor.
 * Runs the client-side format check immediately, then (500ms after typing stops) the server
 * availability check. Consumer-specific concerns stay in the consumers via `skip` — signup passes
 * `skip: !value` and layers its own "required" message; profile passes `skip: value === current`.
 *
 * @param {string} value current subdomain input
 * @param {{ skip?: boolean }} [options] when `skip` is true, no validation runs and state resets to idle
 * @returns {{ status: null|'checking'|'available'|'taken', error: string|null }}
 */
export function useSubdomainAvailability(value, { skip = false } = {}) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (skip || !value) {
      setStatus(null);
      setError(null);
      return;
    }

    const formatError = validateSubdomainFormat(value);
    if (formatError) {
      setStatus(null);
      setError(formatError);
      return;
    }

    setError(null);
    setStatus('checking');
    debounceTimer.current = setTimeout(async () => {
      try {
        const result = await checkSubdomain(value);
        setStatus(result?.data?.available ? 'available' : 'taken');
      } catch {
        setStatus(null);
      }
    }, 500);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [value, skip]);

  return { status, error };
}
